// Meta Pixel (Facebook / Instagram Ads)
// Chargé uniquement en web, et uniquement après consentement publicitaire explicite.
// Chaque événement porte un eventId partagé avec la Conversions API côté serveur
// (functions/metaCapi.js) pour que Meta déduplique les doublons navigateur/serveur.

import { isWeb } from './platform';

const PIXEL_ID = import.meta.env.VITE_FB_PIXEL_ID || '';
const ADS_CONSENT_KEY = 'ads_consent';

let loaded = false;

/** True quand l'utilisateur a explicitement accepté les cookies publicitaires */
export function hasAdsConsent() {
    try {
        return localStorage.getItem(ADS_CONSENT_KEY) === 'granted';
    } catch {
        return false;
    }
}

/** Identifiant unique par événement, sert à la déduplication pixel / CAPI */
export function newEventId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `evt-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/** Lecture d'un cookie navigateur (utilisé pour _fbp / _fbc) */
function readCookie(name) {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Paramètres de correspondance à transmettre à la Conversions API.
 * _fbp est posé par le pixel, _fbc dérive du paramètre fbclid de l'URL d'arrivée.
 */
export function getMatchingParams() {
    return {
        fbp: readCookie('_fbp'),
        fbc: readCookie('_fbc'),
        eventSourceUrl: typeof window !== 'undefined' ? window.location.href : null,
    };
}

/** Injecte fbevents.js et initialise le pixel. Idempotent. */
export function initMetaPixel() {
    if (loaded || !isWeb || !PIXEL_ID || !hasAdsConsent()) return;
    if (typeof window === 'undefined') return;

    // Stub fbq : les appels émis avant le chargement du script sont mis en file
    if (!window.fbq) {
        const fbq = function (...args) {
            if (fbq.callMethod) fbq.callMethod(...args);
            else fbq.queue.push(args);
        };
        fbq.push = fbq;
        fbq.loaded = true;
        fbq.version = '2.0';
        fbq.queue = [];
        window.fbq = fbq;
        window._fbq = window._fbq || fbq;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);

    window.fbq('init', PIXEL_ID);
    loaded = true;
}

/** Enregistre le consentement publicitaire et démarre le pixel dans la foulée */
export function grantAdsConsent() {
    try {
        localStorage.setItem(ADS_CONSENT_KEY, 'granted');
    } catch {
        // stockage indisponible (navigation privée stricte) : on ne track pas
        return;
    }
    initMetaPixel();
}

/** Refus explicite : aucun pixel ne sera chargé */
export function denyAdsConsent() {
    try {
        localStorage.setItem(ADS_CONSENT_KEY, 'denied');
    } catch {
        // rien à faire, l'absence de consentement est déjà l'état par défaut
    }
}

/**
 * Envoie un événement standard Meta.
 * @param {string} eventName - nom standard Meta ('PageView', 'Purchase', ...)
 * @param {object} params - paramètres de l'événement (value, currency, contents...)
 * @param {string} [eventId] - identifiant de déduplication, à partager avec la CAPI
 */
export function metaTrack(eventName, params = {}, eventId = null) {
    if (!isWeb || !PIXEL_ID || !hasAdsConsent()) return;
    initMetaPixel();
    if (typeof window.fbq !== 'function') return;

    if (eventId) {
        window.fbq('track', eventName, params, { eventID: eventId });
    } else {
        window.fbq('track', eventName, params);
    }
}

/** Événement personnalisé (hors liste standard Meta) */
export function metaTrackCustom(eventName, params = {}) {
    if (!isWeb || !PIXEL_ID || !hasAdsConsent()) return;
    initMetaPixel();
    if (typeof window.fbq !== 'function') return;
    window.fbq('trackCustom', eventName, params);
}
